// Header component - handles scroll behavior, language switcher
import { setLanguage, getLanguage, t } from '../../utils/i18n.js';
import { getCurrentLanguage, addLanguageToPath } from '../../shared/utils/urlUtils.ts';

export class Header {
  constructor() {
    this.header = document.getElementById('header');
    this.lastScrollY = window.scrollY;
    this.isScrollingDown = false;
    this.languageToggle = document.getElementById('languageToggle');
    this.languageSwitcher = this.languageToggle?.closest('.language-switcher');
    this.languageDropdown = this.languageSwitcher?.querySelector('.language-dropdown');
    
    this.supportedLanguages = [
      { code: 'en', name: 'English' },
      { code: 'hu', name: 'Magyar' },
      { code: 'ar', name: 'العربية' },
      { code: 'fr', name: 'Français' },
      { code: 'zh-cn', name: '简体中文' },
      { code: 'ko', name: '한국어' },
      { code: 'it', name: 'Italiano' },
      { code: 'es', name: 'Español' },
      { code: 'ja', name: '日本語' },
      { code: 'ru', name: 'Русский' },
      { code: 'tr', name: 'Türkçe' },
      { code: 'pt-br', name: 'Português (Brasil)' },
    ];
    
    this.init();
  }

  init() {
    // Scroll behavior
    this.handleScroll();
    window.addEventListener('scroll', () => this.handleScroll(), { passive: true });
    
    // Language switcher
    if (this.languageToggle && this.languageDropdown) {
      this.populateLanguageDropdown();
      
      this.languageToggle.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleLanguageDropdown();
      });
      
      // Close language dropdown when clicking outside
      document.addEventListener('click', () => {
        if (this.languageSwitcher?.classList.contains('open')) {
          this.closeLanguageDropdown();
        }
      });
    }
  }

  handleScroll() {
    const currentScrollY = window.scrollY;
    const isScrollingDown = currentScrollY > this.lastScrollY;
    const scrollThreshold = 100;
    
    // Add scrolled class for background
    if (currentScrollY > 10) {
      this.header.classList.add('scrolled');
    } else {
      this.header.classList.remove('scrolled');
    }
    
    // Hide/show header on scroll
    if (currentScrollY > scrollThreshold) {
      if (isScrollingDown && !this.isScrollingDown) {
        this.header.classList.add('hidden');
        this.isScrollingDown = true;
      } else if (!isScrollingDown && this.isScrollingDown) {
        this.header.classList.remove('hidden');
        this.isScrollingDown = false;
      }
    } else {
      this.header.classList.remove('hidden');
      this.isScrollingDown = false;
    }
    
    this.lastScrollY = currentScrollY;
  }

  toggleLanguageDropdown() {
    this.languageSwitcher?.classList.toggle('open');
  }

  closeLanguageDropdown() {
    this.languageSwitcher?.classList.remove('open');
  }

  populateLanguageDropdown() {
    if (!this.languageDropdown || !this.languageToggle) return;
    
    const currentLang = getCurrentLanguage();
    
    // Find current language name and update toggle button
    const currentLangInfo = this.supportedLanguages.find(lang => lang.code === currentLang);
    if (currentLangInfo) {
      const languageText = this.languageToggle.querySelector('.language-text');
      if (languageText) {
        languageText.textContent = currentLangInfo.name;
      } else {
        // Fallback if structure changed
        this.languageToggle.textContent = currentLangInfo.name;
      }
    }
    
    // Clear existing options
    this.languageDropdown.innerHTML = '';
    
    // Add language options
    this.supportedLanguages.forEach(lang => {
      const button = document.createElement('button');
      button.textContent = lang.name;
      button.dataset.lang = lang.code;
      if (lang.code === currentLang) {
        button.classList.add('active');
      }
      button.addEventListener('click', (e) => {
        e.stopPropagation();
        this.handleLanguageChange(lang.code);
      });
      this.languageDropdown.appendChild(button);
    });
  }

  handleLanguageChange(langCode) {
    setLanguage(langCode);
    this.closeLanguageDropdown();
    // Update button text immediately
    const selectedLang = this.supportedLanguages.find(lang => lang.code === langCode);
    if (selectedLang && this.languageToggle) {
      const languageText = this.languageToggle.querySelector('.language-text');
      if (languageText) {
        languageText.textContent = selectedLang.name;
      } else {
        // Fallback if structure changed
        this.languageToggle.textContent = selectedLang.name;
      }
    }
    // Update URL with new language and reload page
    const currentPath = window.location.pathname;
    const newPath = addLanguageToPath(langCode, currentPath);
    if (newPath !== currentPath) {
      window.location.href = newPath;
    } else {
      // Reload page to ensure all content is updated
      window.location.reload();
    }
  }
}










